import { Component, inject, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { AbstractControl, FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { lastValueFrom, map } from 'rxjs';
import { BannerComponent } from 'app/shared/components/banner/banner.component';
import { ButtonComponent } from 'app/shared/components/button/button.component';
import { MainLayoutComponent } from 'app/shared/components/layouts/main-layout/main-layout.component';
import { StepperComponent } from 'app/shared/components/stepper/stepper.component';
import { Themes } from 'themes';
import { BackendService } from 'app/shared/services/backend.service';
import { LandingpageConfigurationStateService } from 'app/shared/services/state/landingpage-configuration-state.service';
import { BackendErrorType } from 'app/shared/services/errors/backend.errors';
import { AutocompleteValue } from 'app/shared/models/autocomplete-value.model';
import { RequiredFieldsHintComponent } from 'app/shared/components/required-fields-hint/required-fields-hint.component';

@Component({
  selector: 'ez-verify-email',
  imports: [
    MainLayoutComponent,
    BannerComponent,
    ButtonComponent,
    ReactiveFormsModule,
    StepperComponent,
    RequiredFieldsHintComponent,
  ],
  templateUrl: './verify-email.component.html',
  styleUrl: './verify-email.component.scss',
})
export class VerifyEmailComponent {
  protected readonly CONTROL_NAME = 'emailVerificationCode';

  private readonly router = inject(Router);
  private readonly formBuilder = inject(FormBuilder);
  private readonly backendService = inject(BackendService);

  readonly organisation = inject(LandingpageConfigurationStateService).organisation;
  readonly verifyEmailCodeRequestIsLoading = signal(false);

  readonly form = this.formBuilder.group({
    [this.CONTROL_NAME]: [
      null,
      {
        validators: [Validators.required],
      },
    ],
  });

  readonly errorMessage = toSignal(
    this.emailVerificationCodeControl.events.pipe(map(() => this.currentErrorMessage())),
    { initialValue: null },
  );

  get emailVerificationCodeControl(): AbstractControl {
    return this.form.controls[this.CONTROL_NAME];
  }

  private currentErrorMessage(): string | null {
    const control = this.emailVerificationCodeControl;
    if (!control.invalid || !(control.dirty || control.touched)) {
      return null;
    }
    if (control.hasError('required')) {
      return 'Fehler: Dies ist ein Pflichtfeld. Bitte geben Sie Ihren persönlichen Verifizierungscode an.';
    }
    if (control.hasError('codeMismatch')) {
      return 'Fehler: Code stimmt nicht mit dem Verifizierungscode überein.';
    }
    return null;
  }

  onSubmit() {
    const emailVerificationCode = this.emailVerificationCodeControl.value;
    this.verifyEmailCodeRequestIsLoading.set(true);
    lastValueFrom(this.backendService.verifyEmailCode(emailVerificationCode))
      .then(() => {
        this.router.navigate(['summary']);
      })
      .catch((error) => {
        if (error.message === BackendErrorType.ALREADY_SENT) {
          this.router.navigate(['already-sent']);
        } else if (error.message === BackendErrorType.VERIFICATION_ERROR) {
          this.router.navigate(['verification-error']);
        } else if (error.message === BackendErrorType.WRONG_MAIL_CODE) {
          this.emailVerificationCodeControl.setErrors({ codeMismatch: true });
        }
      })
      .finally(() => {
        this.verifyEmailCodeRequestIsLoading.set(false);
      });
  }

  protected readonly Themes = Themes;
  protected readonly AutocompleteValue = AutocompleteValue;
}
